import type { FormEvent } from 'react';
import { useState } from 'react';

import { CLICK_IDS } from '@sunflower-games/analytics-events';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/buttons/Button';
import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import { FormInput } from '../../../components/FormInput/FormInput';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { analytics } from '../../../services/analytics';
import { useStores } from '../../../stores/useStores';
import { getErrorMessage } from '../../../utils/errors';
import { getEmailErrorMessageMap } from '../SignUpRegister/constants';
import { useOpenAuthWizard } from '../useOpenAuthWizard';

import styles from './ResetPassword.module.scss';

interface ResetPasswordForm extends HTMLFormElement {
  elements: HTMLFormControlsCollection & {
    email: HTMLInputElement;
  };
}

export const ResetPassword = observer(({ nextStep }: WizardChildProps) => {
  const { t } = useTranslation();
  const { authStore, portalStore } = useStores();

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();
  const isDirectLanding = authStore.isDirectLanding();

  const [error, setError] = useState<string>('');
  const isProcessing = computed(() => authStore.pending('sendPasswordResetLink')).get();

  const handleSubmit = async (event: FormEvent<ResetPasswordForm>) => {
    event.preventDefault();
    const { email } = event.currentTarget.elements;

    try {
      await authStore.sendPasswordResetLink(email.value);
      nextStep();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const handleOpenSignUp = () => {
    void analytics.sendEvent('sign_up', {
      step_name: 'sign_up_start',
      button_location: 'reset_password',
      event_action: 'click',
      click_id: CLICK_IDS.AUTH_FLOW.OPEN_SIGN_UP_FROM_RESET_PASSWORD,
      triggered_by: 'click',
      button_name: 'sign_up',
      landingKey,
    });

    openAuthWizard('signUpWizard', {
      triggeredBy: 'reset_password',
      force: true,
      persistent: isDirectLanding,
      landingKey: landingKey ?? undefined,
    });
  };

  const handleGoBack = () => {
    void analytics.sendEvent('log_in', {
      step_name: 'log_in_platform',
      event_action: 'impression',
      triggered_by: 'reset_password',
      landingKey,
    });

    openAuthWizard('logInWizard', {
      triggeredBy: 'reset_password',
      force: true,
      persistent: isDirectLanding,
      landingKey: landingKey ?? undefined,
    });
  };

  return (
    <>
      <DialogHeader
        closeBtnDataTestId="reset-password-close-btn"
        goBackBtnDataTestId="reset-password-back-btn"
        title={t('auth.resetPassword')}
        onClose={portalStore.closePortal}
        onGoBack={handleGoBack}
      />

      <DialogLoader isProcessing={isProcessing} />

      <DialogContent>
        <form onSubmit={handleSubmit}>
          <p>{t('auth.enterEmailInstructions')}</p>

          <div className={`form-field ${styles.resetPwFormField}`}>
            <FormInput
              required
              data-testid="reset-password-email-input"
              disabled={isProcessing}
              errorMessageMap={getEmailErrorMessageMap(t)}
              id="email"
              name="email"
              placeholder={t('forms.emailAddress')}
              type="email"
            />
          </div>

          <Button
            btnType="main"
            className={styles.resetBtn}
            data-testid="reset-passwod-btn"
            disabled={isProcessing}
            type="submit"
          >
            {t('auth.resetPassword')}
          </Button>

          <ErrorMessage>{error}</ErrorMessage>

          <div className="animation-slide-down form-links">
            <span>{t('auth.dontHaveAccountQuestion')}</span>

            <span className="text-link" onClick={handleOpenSignUp}>
              {t('auth.signUp')}
            </span>
          </div>
        </form>
      </DialogContent>
    </>
  );
});
